/* Four gestures that need a hand rather than a stylesheet: the shelf rows
   can be walked with arrows or dragged and drift on their own while nobody
   is touching them, a pack tilts toward the pointer, and a journal cover
   follows the pointer along its row. Each one is a pointer thing, so each one
   checks for a pointer first and does nothing on a phone or for a visitor who
   has asked for less motion. */
(() => {
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- the shelf ---- */
  const shelves = () => {
    document.querySelectorAll('.sa-shelf__row').forEach((row) => {
      const track = row.querySelector('.sa-shelf__track');
      const label = row.querySelector('.sa-shelf__label');
      if (!track || !label || row.dataset.saShelfBound) return;
      row.dataset.saShelfBound = '1';
      const shelf = row.closest('.sa-shelf');
      const words = (shelf && shelf.dataset) || {};

      /* one step is one pack and the gap after it */
      const step = () => {
        const tile = track.querySelector('.sa-shelf__tile');
        if (!tile) return track.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        return tile.getBoundingClientRect().width + gap;
      };

      const nav = document.createElement('div');
      nav.className = 'sa-shelf__nav';
      /* the arrows name the track they move, so a screen reader can say what
         "Next packs" is next of */
      if (!track.id) track.id = 'sa-track-' + Math.random().toString(36).slice(2, 8);
      const make = (dir) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sa-shelf__arrow sa-shelf__arrow--' + dir;
        b.setAttribute('aria-label', dir === 'prev' ? (words.prevLabel || 'Previous') : (words.nextLabel || 'Next'));
        b.setAttribute('aria-controls', track.id);
        b.addEventListener('click', () => {
          /* a drifting row is a loop: it is first put back inside its own
             length, so the step never runs off the cloned end */
          if (track._saNormalize) track._saNormalize(dir, step());
          track.scrollBy({ left: dir === 'prev' ? -step() : step(), behavior: calm.matches ? 'auto' : 'smooth' });
        });
        return b;
      };
      const prev = make('prev');
      const next = make('next');
      nav.append(prev, next);
      label.append(nav);

      const state = () => {
        /* a loop has no ends to reach */
        if (track.dataset.saLoop) { prev.disabled = false; next.disabled = false; return; }
        const max = track.scrollWidth - track.clientWidth - 1;
        prev.disabled = track.scrollLeft <= 0;
        next.disabled = track.scrollLeft >= max;
      };
      track._saState = state;
      track.addEventListener('scroll', state, { passive: true });
      window.addEventListener('resize', state, { passive: true });
      state();

      /* dragging, for a mouse: a scrollbar is honest but a pack is nicer to
         pull. A click that moved less than a few pixels is still a click. */
      if (!fine.matches) return;
      let down = null;
      let moved = false;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = { x: e.clientX, left: track.scrollLeft };
        moved = false;
      });
      track.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - down.x;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          track.classList.add('is-dragging');
          track.setPointerCapture(e.pointerId);
        }
        if (moved) track.scrollLeft = down.left - dx;
      });
      const release = (e) => {
        if (!down) return;
        down = null;
        if (moved) {
          track.classList.remove('is-dragging');
          try { track.releasePointerCapture(e.pointerId); } catch (err) { /* already released */ }
          /* the click that ends a drag is not a click */
          const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          track.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(() => track.removeEventListener('click', swallow, { capture: true }), 0);
        }
      };
      track.addEventListener('pointerup', release);
      track.addEventListener('pointercancel', release);
    });
  };

  /* ---- the shelf drifts ----
     Left alone, a row moves on its own, very slowly, the way a display turns
     in a shop window: the first row toward the left, the next toward the
     right, and so on down the page. The row is cloned after itself as many
     times as the screen needs and the scroll position is wound back by one
     row's length whenever it passes it, so the walk never reaches an end
     and the arrows never go grey. It stops the moment a pointer is over it,
     or a key has put focus in it, or the tab is hidden, or the row is off
     the screen, and starts again when that is over. A phone is not given
     this: there the row is swiped, and a finger and a motor would fight. */
  const drift = () => {
    if (!fine.matches || calm.matches) return;
    let n = 0;
    document.querySelectorAll('.sa-shelf__row').forEach((row) => {
      const shelf = row.closest('.sa-shelf');
      const track = row.querySelector('.sa-shelf__track');
      if (!shelf || !track || track.dataset.saLoop || shelf.dataset.drift === 'off') return;
      const tiles = [...track.children].filter((el) => el.classList.contains('sa-shelf__tile'));
      if (tiles.length < 2) return;
      const speed = Math.max(4, parseFloat(shelf.dataset.drift) || 20);
      const dir = n % 2 === 0 ? 1 : -1;
      n += 1;

      /* one copy of the row is one length; the screen needs the row plus
         enough copies to be covered at any position, plus one to step into */
      const first = tiles[0];
      const one = () => tiles.reduce((w, t) => w + t.getBoundingClientRect().width, 0)
        + tiles.length * (parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0);
      const copies = Math.ceil(track.clientWidth / one()) + 1;
      const clones = [];
      for (let c = 0; c < copies; c += 1) {
        tiles.forEach((tile) => {
          const clone = tile.cloneNode(true);
          clone.classList.add('sa-shelf__tile--clone');
          clone.setAttribute('aria-hidden', 'true');
          clone.dataset.saClone = '1';
          clone.querySelectorAll('quick-add-modal').forEach((m) => m.remove());
          clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
          clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
          clone.querySelectorAll('video').forEach((v) => {
            delete v.dataset.saBackdropStarted;
            delete v.dataset.saBackdropWatched;
            v.removeAttribute('data-sa-backdrop-ready');
          });
          /* the add button on a copy presses the one on the original, which
             is the one the section's script listens to */
          clone.addEventListener('click', (e) => {
            const b = e.target.closest('[data-add-to-cart]');
            if (!b) return;
            e.preventDefault();
            const real = tile.querySelector('[data-add-to-cart]');
            if (real) real.click();
          });
          clones.push(clone);
        });
      }
      track.append(...clones);
      track.dataset.saLoop = '1';
      track.classList.add('is-looping');
      if (window.saBackdropWatch) window.saBackdropWatch(track);
      if (track._saState) track._saState();

      /* the length is measured, not computed: the distance from the first
         tile to its first copy is exactly one row, whatever the gap rounds to */
      const length = () => clones[0].offsetLeft - first.offsetLeft;

      let pos = dir > 0 ? 0 : length();
      track.scrollLeft = pos;
      let frame = 0;
      let last = 0;
      let running = false;
      let hovered = false;
      let focused = false;
      let seen = true;

      const tick = (now) => {
        if (!running) return;
        const dt = last ? Math.min(48, now - last) : 16;
        last = now;
        const w = length();
        pos += (dir * speed * dt) / 1000;
        if (pos >= w) pos -= w;
        if (pos < 0) pos += w;
        track.scrollLeft = pos;
        frame = requestAnimationFrame(tick);
      };
      const wanted = () => !hovered && !focused && seen && !document.hidden;
      const settle = () => {
        if (wanted() && !running) {
          running = true;
          last = 0;
          /* start from wherever the visitor left it */
          pos = track.scrollLeft;
          frame = requestAnimationFrame(tick);
        } else if (!wanted() && running) {
          running = false;
          cancelAnimationFrame(frame);
        }
      };

      /* the arrows and a drag put the row back inside one length first */
      track._saNormalize = (arrow, step) => {
        const w = length();
        if (arrow === 'next' && track.scrollLeft >= w) track.scrollLeft -= w;
        if (arrow === 'prev' && track.scrollLeft < step) track.scrollLeft += w;
        pos = track.scrollLeft;
      };

      row.addEventListener('pointerenter', () => { hovered = true; settle(); });
      row.addEventListener('pointerleave', () => { hovered = false; settle(); });
      track.addEventListener('focusin', () => { focused = true; settle(); });
      track.addEventListener('focusout', () => {
        setTimeout(() => { focused = track.contains(document.activeElement); settle(); }, 0);
      });
      document.addEventListener('visibilitychange', settle);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver((entries) => {
          entries.forEach((e) => { seen = e.isIntersecting; });
          settle();
        }, { rootMargin: '80px 0px' }).observe(row);
      }
      /* the length changes with the screen; the position is wound back into it */
      window.addEventListener('resize', () => {
        const w = length();
        if (w > 0 && track.scrollLeft >= w) { track.scrollLeft -= w; pos = track.scrollLeft; }
      }, { passive: true });
      settle();
    });
  };

  /* ---- a pack tilts toward the pointer ---- */
  const tilt = () => {
    if (!fine.matches || calm.matches) return;
    const targets = document.querySelectorAll('.sa-shelf__media, .product__media-item.is-active .product__media, .product__media-list .product__media-item:first-child .product__media');
    targets.forEach((el) => {
      if (el.dataset.saTilt) return;
      el.dataset.saTilt = '1';
      let frame = 0;
      const move = (e) => {
        const r = el.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          el.style.transform = 'perspective(90rem) rotateX(' + (-py * 7).toFixed(2) + 'deg) rotateY(' + (px * 7).toFixed(2) + 'deg) scale(1.02)';
        });
      };
      el.addEventListener('pointerenter', () => el.classList.add('is-tilting'));
      el.addEventListener('pointermove', move);
      el.addEventListener('pointerleave', () => {
        cancelAnimationFrame(frame);
        el.classList.remove('is-tilting');
        el.style.transform = '';
      });
    });
  };

  /* ---- a pack on a stage leans toward the pointer over the whole card ---- */
  const stages = () => {
    if (!fine.matches || calm.matches) return;
    document.querySelectorAll('[data-sa-stage]').forEach((stage) => {
      const card = stage.parentElement;
      const figure = stage.querySelector('.sa-stage__figure');
      if (!card || !figure || card.dataset.saStageBound) return;
      card.dataset.saStageBound = '1';
      let frame = 0;
      const move = (e) => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          figure.style.transform = 'perspective(120rem) rotateX(' + (-py * 6).toFixed(2) + 'deg) rotateY(' + (px * 10).toFixed(2) + 'deg) translateX(' + (px * 12).toFixed(1) + 'px)';
        });
      };
      card.addEventListener('pointerenter', () => figure.classList.add('is-tilting'));
      card.addEventListener('pointermove', move);
      card.addEventListener('pointerleave', () => {
        cancelAnimationFrame(frame);
        figure.classList.remove('is-tilting');
        figure.style.transform = '';
      });
    });
  };

  /* ---- the journal cover follows the pointer along its row ---- */
  const covers = () => {
    if (!fine.matches || calm.matches) return;
    document.querySelectorAll('.sa-index').forEach((index) => {
      if (index.dataset.saFloatBound) return;
      index.dataset.saFloatBound = '1';
      const float = document.createElement('div');
      float.className = 'sa-index__float';
      float.setAttribute('aria-hidden', 'true');
      const img = document.createElement('img');
      img.alt = '';
      float.append(img);
      document.body.append(float);
      let target = { x: 0, y: 0 };
      let shown = { x: 0, y: 0 };
      let on = false;
      let frame = 0;
      const tick = () => {
        shown.x += (target.x - shown.x) * 0.18;
        shown.y += (target.y - shown.y) * 0.18;
        // to the right of the pointer, or to its left when the right edge of
        // the screen is nearer than the card is wide
        const w = float.offsetWidth;
        const x = shown.x + 44 + w > window.innerWidth - 16 ? shown.x - 44 - w : shown.x + 44;
        float.style.transform = 'translate(' + x + 'px, ' + (shown.y - float.offsetHeight / 2) + 'px)';
        if (on || Math.abs(target.x - shown.x) > 0.5 || Math.abs(target.y - shown.y) > 0.5) frame = requestAnimationFrame(tick);
      };
      index.querySelectorAll('.sa-index__row').forEach((row) => {
        const src = row.querySelector('.sa-index__cover img');
        if (!src) return;
        row.addEventListener('pointerenter', (e) => {
          img.src = src.currentSrc || src.src;
          row.classList.add('is-floated');
          target = { x: e.clientX, y: e.clientY };
          if (!on) shown = { ...target };
          on = true;
          float.classList.add('is-on');
          cancelAnimationFrame(frame);
          frame = requestAnimationFrame(tick);
        });
        row.addEventListener('pointermove', (e) => { target = { x: e.clientX, y: e.clientY }; });
        row.addEventListener('pointerleave', () => {
          row.classList.remove('is-floated');
          on = false;
          float.classList.remove('is-on');
        });
      });
    });
  };

  /* ---- the pack travels from the tile to the product page ---- */
  const travel = () => {
    if (calm.matches || !('startViewTransition' in document)) return;
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (!link || e.defaultPrevented || e.metaKey || e.ctrlKey || e.button !== 0) return;
      const tile = link.closest('.card-wrapper, .sa-shelf__tile, .cart-empty-picks__item, .same-flavour__card, .sa-index__row');
      if (!tile) return;
      const img = tile.querySelector('img');
      if (!img) return;
      /* only one thing on the page may carry the name when the snapshot is taken */
      document.querySelectorAll('[data-sa-travelling]').forEach((el) => { el.style.viewTransitionName = ''; delete el.dataset.saTravelling; });
      img.style.viewTransitionName = 'sa-pack';
      img.dataset.saTravelling = '1';
    }, true);
    /* a page restored from the back-forward cache keeps the name it was given */
    window.addEventListener('pageshow', () => {
      document.querySelectorAll('[data-sa-travelling]').forEach((el) => { el.style.viewTransitionName = ''; delete el.dataset.saTravelling; });
    });
  };

  const start = () => { shelves(); drift(); tilt(); stages(); covers(); };
  travel();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  /* the theme editor and the section rendering API draw sections again */
  document.addEventListener('shopify:section:load', start);
  /* the shelf of the range on a product page is drawn again once Shopify
     has answered with its recommendations */
  if ('MutationObserver' in window) {
    document.querySelectorAll('product-recommendations').forEach((el) => {
      new MutationObserver(start).observe(el, { childList: true });
    });
  }
})();
